import { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Flame,
  CheckCircle2,
  MessageSquareText,
  Loader2,
  Sparkles,
} from "lucide-react";
import {
  PERKS,
  PERK_PRICE,
  BUNDLE_PRICE,
  computeTotal,
} from "@/components/forest/perks";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OrderForm() {
  const [robloxUsername, setRobloxUsername] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedPerks, setSelectedPerks] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const allSelected = selectedPerks.length === PERKS.length;
  const total = useMemo(() => computeTotal(selectedPerks), [selectedPerks]);
  const undiscountedTotal = selectedPerks.length * PERK_PRICE;
  const savings = allSelected ? undiscountedTotal - BUNDLE_PRICE : 0;

  const togglePerk = (id) => {
    setSelectedPerks((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    setSelectedPerks(allSelected ? [] : PERKS.map((p) => p.id));
  };

  const validate = () => {
    if (!robloxUsername.trim()) {
      toast.error("Please enter your Roblox username.");
      return false;
    }
    if (robloxUsername.trim().length < 3) {
      toast.error("That Roblox username looks too short.");
      return false;
    }
    const digits = phoneNumber.replace(/\D/g, "");
    if (digits.length < 7) {
      toast.error("Please enter a valid phone number.");
      return false;
    }
    if (selectedPerks.length === 0) {
      toast.error("Pick at least one perk to order.");
      return false;
    }
    return true;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        roblox_username: robloxUsername.trim(),
        phone_number: phoneNumber.trim(),
        selected_perks: selectedPerks,
      });
      setSuccess(data);
      toast.success("Order placed! Check your phone for our text.");
      setRobloxUsername("");
      setPhoneNumber("");
      setSelectedPerks([]);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Something went wrong. Try again.";
      toast.error(typeof detail === "string" ? detail : "Order failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="order"
      data-testid="order-section"
      className="relative py-24 md:py-32 border-t border-emerald-900/30"
    >
      {/* Firefly background image */}
      <div
        className="absolute inset-0 -z-10 opacity-20"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/31148663/pexels-photo-31148663.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 -z-10 bg-[#040B11]/70" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-300 font-bold">
            Place your order
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl mt-3 text-white leading-tight">
            Pick your perks.<br />
            <span className="text-amber-400">We'll handle the rest.</span>
          </h2>
        </div>

        <div
          data-testid="order-card"
          className="mt-12 backdrop-blur-xl bg-[#081622]/70 border border-emerald-900/40 rounded-3xl p-7 sm:p-10 shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
        >
          {success ? (
            <div data-testid="order-success" className="text-center py-6">
              <div className="mx-auto w-16 h-16 rounded-full grid place-items-center bg-emerald-900/40 border border-amber-500/40 ember-pulse">
                <CheckCircle2 className="w-8 h-8 text-amber-400" />
              </div>
              <h3 className="font-serif text-3xl text-white mt-6">
                You're in, {success.roblox_username}!
              </h3>
              <p className="mt-4 text-emerald-100/80 text-base md:text-lg max-w-lg mx-auto">
                We just got your order for{" "}
                <span className="text-amber-400 font-bold">
                  ${success.price_usd.toFixed(2)}
                </span>
                . Keep an eye on your phone — we'll text you our Cash App tag
                in a moment. Once you send it over, we'll add you on Roblox
                and drop in with your perks.
              </p>
              <div className="mt-8 inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-emerald-900/30 border border-emerald-700/40 text-emerald-100">
                <MessageSquareText className="w-5 h-5 text-amber-400" />
                <span className="text-sm">
                  Text incoming to{" "}
                  <strong className="text-white">{success.phone_number}</strong>
                </span>
              </div>
              <div className="mt-8">
                <Button
                  data-testid="place-another-btn"
                  onClick={() => setSuccess(null)}
                  variant="ghost"
                  className="text-emerald-100 hover:text-amber-400 hover:bg-emerald-900/30"
                >
                  Place another order →
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-7">
              {/* Perk selection */}
              <div>
                <div className="flex items-center justify-between gap-3">
                  <Label className="text-sm uppercase tracking-[0.15em] text-emerald-300 font-bold">
                    Pick your perks
                  </Label>
                  <button
                    type="button"
                    data-testid="toggle-bundle-btn"
                    onClick={toggleAll}
                    className={`text-xs px-3 py-1.5 rounded-full border transition ${
                      allSelected
                        ? "bg-amber-500/20 border-amber-400/60 text-amber-300"
                        : "bg-emerald-900/30 border-emerald-700/50 text-emerald-100 hover:border-amber-500/60 hover:text-amber-300"
                    }`}
                  >
                    <Sparkles className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                    {allSelected ? "Bundle selected ($20)" : "Grab the bundle — $20"}
                  </button>
                </div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {PERKS.map((p) => {
                    const Icon = p.icon;
                    const checked = selectedPerks.includes(p.id);
                    return (
                      <label
                        key={p.id}
                        data-testid={`perk-option-${p.id}`}
                        htmlFor={`perk-${p.id}`}
                        className={`flex items-center gap-3 p-4 rounded-2xl border cursor-pointer transition ${
                          checked
                            ? "bg-amber-500/10 border-amber-500/50"
                            : "bg-[#040B11]/60 border-emerald-900/50 hover:border-amber-500/30"
                        }`}
                      >
                        <Checkbox
                          id={`perk-${p.id}`}
                          data-testid={`perk-checkbox-${p.id}`}
                          checked={checked}
                          onCheckedChange={() => togglePerk(p.id)}
                          className="border-emerald-700 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500 data-[state=checked]:text-[#1a0e00]"
                        />
                        <Icon className="w-5 h-5 text-amber-400 shrink-0" />
                        <span className="flex-1 text-white text-sm font-semibold">
                          {p.title}
                        </span>
                        <span className="text-amber-400 font-serif text-base">
                          ${PERK_PRICE}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Contact */}
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label
                    htmlFor="roblox_username"
                    className="text-sm uppercase tracking-[0.15em] text-emerald-300 font-bold"
                  >
                    Roblox username
                  </Label>
                  <Input
                    id="roblox_username"
                    data-testid="roblox-username-input"
                    placeholder="e.g. ForestRunner99"
                    value={robloxUsername}
                    onChange={(e) => setRobloxUsername(e.target.value)}
                    className="mt-2 h-14 text-base bg-[#040B11]/80 border-emerald-900/50 text-white placeholder:text-emerald-100/30 focus-visible:border-amber-500 focus-visible:ring-amber-500/40 rounded-xl"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="phone_number"
                    className="text-sm uppercase tracking-[0.15em] text-emerald-300 font-bold"
                  >
                    Phone number
                  </Label>
                  <Input
                    id="phone_number"
                    data-testid="phone-number-input"
                    placeholder="(555) 555-5555"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    inputMode="tel"
                    className="mt-2 h-14 text-base bg-[#040B11]/80 border-emerald-900/50 text-white placeholder:text-emerald-100/30 focus-visible:border-amber-500 focus-visible:ring-amber-500/40 rounded-xl"
                    autoComplete="tel"
                  />
                </div>
              </div>

              {/* Order summary */}
              <div
                data-testid="order-summary"
                className="flex items-center justify-between gap-4 px-5 py-4 rounded-2xl bg-emerald-900/20 border border-emerald-900/40"
              >
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-emerald-300 font-bold">
                    {allSelected
                      ? "Full forest bundle"
                      : selectedPerks.length === 0
                      ? "No perks selected yet"
                      : `${selectedPerks.length} perk${selectedPerks.length > 1 ? "s" : ""} selected`}
                  </p>
                  <p className="text-sm text-emerald-100/70 mt-0.5">
                    {savings > 0
                      ? `You're saving $${savings} with the bundle.`
                      : "Cash App only — we'll text you the $cashtag."}
                  </p>
                </div>
                <div className="flex items-baseline gap-2">
                  {allSelected && (
                    <span
                      data-testid="order-undiscounted-total"
                      className="text-emerald-100/40 line-through font-serif text-lg"
                    >
                      ${undiscountedTotal}
                    </span>
                  )}
                  <div
                    data-testid="order-total"
                    className="font-serif text-3xl text-amber-400"
                  >
                    ${total}
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="submit-order-btn"
                disabled={submitting || selectedPerks.length === 0}
                className="w-full ember-pulse h-16 rounded-2xl bg-[#FF7A00] hover:bg-[#FF9933] disabled:opacity-60 disabled:cursor-not-allowed border border-amber-400/50 text-white text-base sm:text-lg font-bold shadow-[0_0_40px_rgba(255,122,0,0.4)]"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Placing your order…
                  </>
                ) : (
                  <>
                    <Flame className="w-5 h-5 mr-2" />
                    {selectedPerks.length === 0
                      ? "Pick a perk to continue"
                      : `Place order — $${total}`}
                  </>
                )}
              </Button>

              <p
                data-testid="cashapp-only-note"
                className="text-xs text-emerald-100/60 text-center"
              >
                <span className="text-amber-400 font-bold">Cash App only.</span>{" "}
                We'll text you our $cashtag — no other payment methods accepted.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
